import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { api, useAuth } from '../context/AuthContext';
import { 
  Building2, 
  Users, 
  Euro, 
  TrendingUp, 
  FileText, 
  Bell, 
  Copy,
  ChevronRight,
  CheckCircle2,
  Clock,
  AlertCircle,
  Plus,
  Download,
  ExternalLink,
  PieChart,
  BarChart3,
  Calendar,
  MessageSquare,
  Loader2
} from 'lucide-react';

const SponsorDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [summary, setSummary] = useState({
    active_raises: 0,
    total_raised: 0,
    total_investors: 0,
    pending_actions: 0,
    sponsor_brought_investors: 0,
    platform_sourced_investors: 0
  });
  const [raises, setRaises] = useState([]);
  const [capTable, setCapTable] = useState([]);
  const [capTableSummary, setCapTableSummary] = useState({});
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [pendingActions, setPendingActions] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [updates, setUpdates] = useState([]);

  // Fetch dashboard data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch summary
        const summaryRes = await api.get('/sponsor/dashboard/summary');
        setSummary(summaryRes.data);
        
        // Fetch raises
        const raisesRes = await api.get('/sponsor/dashboard/raises');
        setRaises(raisesRes.data.raises || []);
        
        // Set first project as selected for cap table
        if (raisesRes.data.raises?.length > 0) {
          setSelectedProjectId(raisesRes.data.raises[0].id);
        }
        
        // Fetch pending actions
        const actionsRes = await api.get('/sponsor/dashboard/pending-actions');
        setPendingActions(actionsRes.data.actions || []);
        
        // Fetch documents
        const docsRes = await api.get('/sponsor/dashboard/documents');
        setDocuments(docsRes.data.documents || []);
        
        // Fetch updates
        const updatesRes = await api.get('/sponsor/dashboard/updates');
        setUpdates(updatesRes.data.updates || []);
        
      } catch (error) {
        console.error('Error fetching sponsor dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Fetch cap table when project is selected
  useEffect(() => {
    if (!selectedProjectId) return;
    
    const fetchCapTable = async () => {
      try {
        const res = await api.get(`/sponsor/dashboard/captable/${selectedProjectId}`);
        setCapTable(res.data.cap_table || []);
        setCapTableSummary(res.data.summary || {});
      } catch (error) {
        console.error('Error fetching cap table:', error);
      }
    };
    
    fetchCapTable();
  }, [selectedProjectId]);

  // Calculate totals
  const totalInvestors = summary.total_investors || 0;
  const sponsorBrought = summary.sponsor_brought_investors || 0;
  const platformSourced = summary.platform_sourced_investors || totalInvestors;

  if (loading) {
    return (
      <div className="min-h-screen bg-background" data-testid="sponsor-dashboard-page">
        <Header />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="sponsor-dashboard-page">
      <Header />

      {/* Dashboard Header */}
      <section className="pt-6 pb-4 border-b border-white/5">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold">Sponsor Dashboard</h1>
              <p className="text-foreground-muted text-sm mt-1">Manage your raises, investors, and project communications</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="rounded-full" onClick={() => navigate('/submit-project')}>
                <Plus className="h-4 w-4 mr-2" />
                New Project
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* KPI Tiles */}
      <section className="py-6">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="glass p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-foreground-muted">Active Raises</p>
                  <p className="text-xl font-bold">{summary.active_raises}</p>
                </div>
              </div>
            </Card>
            
            <Card className="glass p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Euro className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <p className="text-xs text-foreground-muted">Total Raised</p>
                  <p className="text-xl font-bold">€{summary.total_raised.toLocaleString()}</p>
                </div>
              </div>
            </Card>
            
            <Card className="glass p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs text-foreground-muted">Total Investors</p>
                  <p className="text-xl font-bold">{summary.total_investors}</p>
                </div>
              </div>
            </Card>
            
            <Card className="glass p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <Bell className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-xs text-foreground-muted">Pending Actions</p>
                  <p className="text-xl font-bold">{summary.pending_actions}</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Main Content Tabs */}
      <section className="py-4">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-background-paper/50 p-1 rounded-full inline-flex mb-6">
              <TabsTrigger value="overview" className="rounded-full px-4 py-2 text-sm">Overview</TabsTrigger>
              <TabsTrigger value="investors" className="rounded-full px-4 py-2 text-sm">Cap Table</TabsTrigger>
              <TabsTrigger value="documents" className="rounded-full px-4 py-2 text-sm">Documents</TabsTrigger>
              <TabsTrigger value="updates" className="rounded-full px-4 py-2 text-sm">Updates</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Active Raises */}
                <div className="lg:col-span-2">
                  <Card className="glass p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-primary" />
                        Active Raises
                      </h3>
                    </div>
                    
                    {raises.length === 0 ? (
                      <div className="text-center py-8 text-foreground-muted">
                        <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No active raises yet</p>
                        <Button 
                          className="mt-4 rounded-full"
                          onClick={() => navigate('/submit-project')}
                        >
                          Submit Your First Project
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {raises.map((raise) => (
                          <div 
                            key={raise.id}
                            className="p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors cursor-pointer"
                            onClick={() => navigate(`/projects/${raise.id}`)}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h4 className="font-medium text-sm">{raise.title}</h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
                                    raise.status === 'open' ? 'bg-emerald-500/20 text-emerald-400' : 
                                    raise.status === 'funded' ? 'bg-primary/20 text-primary' :
                                    'bg-amber-500/20 text-amber-400'
                                  }`}>
                                    {raise.status === 'open' ? 'Open' : 
                                     raise.status === 'funded' ? 'Funded' :
                                     raise.status === 'in_execution' ? 'In Execution' : raise.status}
                                  </span>
                                  {raise.days_left > 0 && (
                                    <span className="text-xs text-foreground-muted">{raise.days_left} days left</span>
                                  )}
                                </div>
                              </div>
                              <ChevronRight className="h-5 w-5 text-foreground-muted" />
                            </div>
                            
                            <div className="mt-3">
                              <div className="flex justify-between text-xs mb-1">
                                <span className="text-foreground-muted">€{raise.raised.toLocaleString()} / €{raise.target.toLocaleString()}</span>
                                <span className="font-medium">{raise.progress.toFixed(0)}%</span>
                              </div>
                              <Progress value={raise.progress} className="h-2" />
                            </div>
                            
                            <div className="flex items-center gap-4 mt-3 text-xs text-foreground-muted">
                              <span className="flex items-center gap-1">
                                <Users className="h-3.5 w-3.5" />
                                {raise.investors} investors
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                </div>

                {/* Sidebar */}
                <div>
                  {/* Pending Actions */}
                  <Card className="glass p-5">
                    <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                      <AlertCircle className="h-5 w-5 text-warning" />
                      Pending Actions
                    </h3>
                    
                    {pendingActions.length === 0 ? (
                      <p className="text-sm text-foreground-muted text-center py-4">No pending actions</p>
                    ) : (
                      <div className="space-y-3">
                        {pendingActions.slice(0, 5).map((action) => (
                          <div 
                            key={action.id}
                            className="p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                          >
                            <div className="flex items-start gap-3">
                              <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                action.type === 'investor_kyc' ? 'bg-primary/10' :
                                action.type === 'governance' ? 'bg-accent/10' : 'bg-emerald-500/10'
                              }`}>
                                {action.type === 'investor_kyc' && <Users className="h-4 w-4 text-primary" />}
                                {action.type === 'governance' && <FileText className="h-4 w-4 text-accent" />}
                                {action.type === 'distribution' && <Euro className="h-4 w-4 text-emerald-400" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{action.title}</p>
                                <p className="text-xs text-foreground-muted">{action.project}</p>
                                <p className="text-xs text-foreground-muted mt-1">Due: {action.date}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>

                  {/* Investor Source */}
                  <Card className="glass p-5 mt-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                      <PieChart className="h-5 w-5 text-primary" />
                      Investor Source
                    </h3>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-foreground-muted">Sponsor-brought</span>
                        <span className="text-sm font-medium">{sponsorBrought} investors</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-foreground-muted">Platform-sourced</span>
                        <span className="text-sm font-medium">{platformSourced} investors</span>
                      </div>
                      
                      {totalInvestors > 0 && (
                        <>
                          <div className="w-full h-3 rounded-full bg-white/10 overflow-hidden flex mt-2">
                            <div 
                              className="h-full bg-primary" 
                              style={{ width: `${(sponsorBrought / totalInvestors) * 100}%` }}
                            />
                            <div 
                              className="h-full bg-accent" 
                              style={{ width: `${(platformSourced / totalInvestors) * 100}%` }}
                            />
                          </div>
                          <div className="flex justify-between text-xs text-foreground-muted">
                            <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-primary"></div> Sponsor</span>
                            <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-accent"></div> Platform</span>
                          </div>
                        </>
                      )}
                    </div>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* Cap Table Tab */}
            <TabsContent value="investors">
              <Card className="glass p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    Cap Table Snapshot
                  </h3>
                  <div className="flex items-center gap-2">
                    {raises.length > 1 && (
                      <select 
                        className="bg-background-paper border border-white/10 rounded-lg px-3 py-1.5 text-sm"
                        value={selectedProjectId || ''}
                        onChange={(e) => setSelectedProjectId(e.target.value)}
                      >
                        {raises.map(r => (
                          <option key={r.id} value={r.id}>{r.title}</option>
                        ))}
                      </select>
                    )}
                    <Button variant="outline" size="sm" className="rounded-full">
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </div>
                
                {capTable.length === 0 ? (
                  <p className="text-center py-8 text-foreground-muted">No investors yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-2 font-medium text-foreground-muted">Investor</th>
                          <th className="text-right py-3 px-2 font-medium text-foreground-muted">Tokens</th>
                          <th className="text-right py-3 px-2 font-medium text-foreground-muted">Value</th>
                          <th className="text-right py-3 px-2 font-medium text-foreground-muted">%</th>
                          <th className="text-center py-3 px-2 font-medium text-foreground-muted">Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {capTable.map((investor, idx) => (
                          <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-3 px-2">
                              <div>
                                <p className="font-medium">{investor.name}</p>
                                <p className="text-xs text-foreground-muted">{investor.email}</p>
                              </div>
                            </td>
                            <td className="text-right py-3 px-2">{investor.tokens?.toFixed(2)}</td>
                            <td className="text-right py-3 px-2">€{investor.value?.toLocaleString()}</td>
                            <td className="text-right py-3 px-2">{investor.percentage}%</td>
                            <td className="text-center py-3 px-2">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                investor.source === 'sponsor' ? 'bg-primary/20 text-primary' : 'bg-accent/20 text-accent'
                              }`}>
                                {investor.source}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents">
              <Card className="glass p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    Documents Center
                  </h3>
                  <Button variant="outline" size="sm" className="rounded-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Upload Document
                  </Button>
                </div>
                
                {documents.length === 0 ? (
                  <p className="text-center py-8 text-foreground-muted">No documents yet</p>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div 
                        key={doc.id}
                        className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                            doc.type === 'template' ? 'bg-primary/10' :
                            doc.type === 'report' ? 'bg-accent/10' :
                            doc.type === 'legal' ? 'bg-amber-500/10' : 'bg-emerald-500/10'
                          }`}>
                            <FileText className={`h-5 w-5 ${
                              doc.type === 'template' ? 'text-primary' :
                              doc.type === 'report' ? 'text-accent' :
                              doc.type === 'legal' ? 'text-amber-400' : 'text-emerald-400'
                            }`} />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{doc.name}</p>
                            <p className="text-xs text-foreground-muted">Added {doc.date}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* Updates Tab */}
            <TabsContent value="updates">
              <Card className="glass p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-primary" />
                    Project Updates Manager
                  </h3>
                  <Button className="bg-primary hover:bg-primary-hover rounded-full" size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    New Update
                  </Button>
                </div>
                
                {updates.length === 0 ? (
                  <p className="text-center py-8 text-foreground-muted">No updates yet</p>
                ) : (
                  <div className="space-y-3">
                    {updates.map((update) => (
                      <div 
                        key={update.id}
                        className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                            update.status === 'published' ? 'bg-emerald-500/10' : 'bg-amber-500/10'
                          }`}>
                            {update.status === 'published' ? (
                              <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                            ) : (
                              <Clock className="h-5 w-5 text-amber-400" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium">{update.title}</p>
                            <p className="text-xs text-foreground-muted">{update.project} • {update.date}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            update.status === 'published' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {update.status}
                          </span>
                          <Button variant="outline" size="sm" className="rounded-full">
                            Edit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Clone Project CTA */}
              <Card className="glass p-5 mt-4 border-primary/30">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Copy className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold">Clone Project for Next Raise</h4>
                    <p className="text-sm text-foreground-muted">Reuse your project setup and launch faster</p>
                  </div>
                  <Button className="bg-primary hover:bg-primary-hover rounded-full">
                    Clone Project
                  </Button>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SponsorDashboardPage;
